/**
 * 🎬 Opening Cinematic — Multi-Act Intro
 *
 *   ACT 1 — The Dream (Corridor of Shadows)
 *     pigeon → silver-light tutorial → corridor → chronophage → mirror →
 *     choice (🪞/🌑) → path → gasp → DREAM SPAWN (Mossy Courtyard)
 *
 *   ACT 2 — The Real Awakening (Tomb of Awakening)
 *     fade → tomb wake → Silver Light explains the Tomb →
 *     journals (Cari / Cassandra / Ledger POVs) →
 *     class pick (Phoenix transformation warning) → transformation →
 *     fade
 *
 *   ACT 3 — Dragonious Castle & The Farm
 *     castle wake → Green Thumb book → Silver Light explains the Farm →
 *     bump into General Cari → exit to game
 */
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CLASS_CARDS = [
  {
    id: "warrior",
    icon: "⚔️",
    name: "Warrior",
    tagline: "Steel and stubbornness.",
    blurb: "Heavy armor. Brutal swings. Bleed the world.",
    tone: "border-amber-600/60 hover:border-amber-300 from-amber-950/40 to-black",
  },
  {
    id: "mage",
    icon: "🔮",
    name: "Mage",
    tagline: "Reality is a suggestion.",
    blurb: "Mana arrows. Frost shields. Ash-mist that swallows fields.",
    tone: "border-violet-600/60 hover:border-violet-300 from-violet-950/40 to-black",
  },
  {
    id: "archer",
    icon: "🏹",
    name: "Archer",
    tagline: "Patience. Wind. Inevitability.",
    blurb: "Hybrid striker. Enchanted arrows from impossible distances.",
    tone: "border-emerald-600/60 hover:border-emerald-300 from-emerald-950/40 to-black",
  },
  {
    id: "ash_rogue",
    icon: "🌑",
    name: "Ash Rogue",
    tagline: "Burn the floor. Vanish in the smoke.",
    blurb: "Fast. Fragile. Cursed-fire ash on every blade.",
    tone: "border-rose-600/60 hover:border-rose-300 from-rose-950/40 to-black",
  },
];

export default function OpeningCinematic() {
  const navigate = useNavigate();
  const { isAuthed } = useAuth();
  const [scene, setScene] = useState("pigeon");
  const [choice, setChoice] = useState(null);   // 'mirror' | 'void'
  const [completeResult, setCompleteResult] = useState(null);
  const [tombResult, setTombResult] = useState(null);
  const [classPickResult, setClassPickResult] = useState(null);
  const [introResult, setIntroResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const skippedRef = useRef(false);

  useEffect(() => {
    if (!isAuthed) navigate("/", { replace: true });
  }, [isAuthed, navigate]);

  useEffect(() => {
    if (!isAuthed) return;
    axios.get(`${API}/cinematic/state`).then((r) => {
      if (r.data?.stage === "fully_complete") navigate("/", { replace: true });
    }).catch(() => { /* first-load race */ });
  }, [isAuthed, navigate]);

  // Auto-advance timers — bumped Feb 2026 (~+60% on each scene) per owner
  // feedback that wording was reading too fast. Several scenes also pause
  // for an explicit player tap (silver_light, choice, spawn→tomb, etc.).
  useEffect(() => {
    if (skippedRef.current) return;
    let t = null;
    if (scene === "pigeon")           t = setTimeout(() => setScene("silver_light"), 5000);
    // silver_light pauses for player tap
    else if (scene === "corridor")    t = setTimeout(() => setScene("chronophage"), 5500);
    else if (scene === "chronophage") t = setTimeout(() => setScene("mirror"), 4800);
    else if (scene === "mirror")      t = setTimeout(() => setScene("choice"), 4000);
    // choice pauses for player tap
    else if (scene === "path")        t = setTimeout(() => setScene("gasp"), 3800);
    else if (scene === "gasp")        t = setTimeout(() => setScene("spawn"), 2800);
    // spawn pauses for player tap (→ tomb)
    // tomb pauses for player tap (→ class_pick)
    // class_pick pauses for player tap
    else if (scene === "phoenix_transform") t = setTimeout(() => setScene("castle"), 4200);
    // castle pauses for player tap (→ green_thumb)
    // green_thumb pauses for player tap (→ cari)
    // cari pauses for player tap (→ exit)
    return () => { if (t) clearTimeout(t); };
  }, [scene]);

  const pick = async (c) => {
    if (busy) return;
    setBusy(true);
    setChoice(c);
    try {
      const r = await axios.post(`${API}/cinematic/complete`, { choice: c });
      setCompleteResult(r.data);
    } catch (e) {
      console.error("cinematic complete failed", e);
    } finally {
      setBusy(false);
      setScene("path");
    }
  };

  const goToTomb = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const r = await axios.post(`${API}/cinematic/awaken-at-tomb`);
      setTombResult(r.data);
      setScene("tomb");
    } catch (e) {
      console.error("tomb awakening failed", e);
    } finally {
      setBusy(false);
    }
  };

  const goToClassPick = () => {
    setScene("class_pick");
  };

  const pickClass = async (classId) => {
    if (busy) return;
    setBusy(true);
    try {
      const r = await axios.post(`${API}/cinematic/pick-class`, { class_id: classId });
      setClassPickResult({ ...r.data, picked: classId });
      setScene("phoenix_transform");
    } catch (e) {
      console.error("class pick failed", e);
      alert(e.response?.data?.detail || "Class pick failed — try again.");
    } finally {
      setBusy(false);
    }
  };

  const goToGreenThumb = () => {
    setScene("green_thumb");
  };

  const goToCari = () => {
    setScene("cari");
  };

  const onEnter = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const r = await axios.post(`${API}/cinematic/complete-intro`);
      setIntroResult(r.data);
    } catch (e) {
      console.error("complete-intro failed", e);
    } finally {
      setBusy(false);
      navigate("/", { replace: true });
    }
  };

  const onSkip = async () => {
    // Single-click skip (no confirm modal — Playwright + impatient users both happier).
    // We still default Mirror/Warrior so the user has a coherent profile post-skip.
    if (skippedRef.current) return;
    skippedRef.current = true;
    try {
      await axios.post(`${API}/cinematic/complete`, { choice: "mirror" }).catch(() => {});
      await axios.post(`${API}/cinematic/awaken-at-tomb`).catch(() => {});
      await axios.post(`${API}/cinematic/pick-class`, { class_id: "warrior" }).catch(() => {});
      await axios.post(`${API}/cinematic/complete-intro`).catch(() => {});
    } finally {
      navigate("/", { replace: true });
    }
  };

  return (
    <div
      data-testid="opening-cinematic"
      className="fixed inset-0 z-[100] bg-black text-amber-100 overflow-hidden"
      style={{ fontFamily: "Anton, sans-serif" }}
    >
      <button
        type="button"
        onClick={onSkip}
        data-testid="cinematic-skip-btn"
        className="absolute top-4 right-4 z-50 text-[10px] uppercase tracking-[0.3em] text-neutral-500 hover:text-amber-300 px-3 py-1 border border-neutral-800 rounded-full"
      >
        Skip ›
      </button>

      {/* ════ ACT 1 — THE DREAM ════ */}

      {scene === "pigeon" && (
        <SceneFrame testid="scene-pigeon" bg="from-stone-900 via-stone-800 to-stone-950">
          <div className="text-[10px] uppercase tracking-[0.5em] text-neutral-500 animate-[colFadeIn_0.8s_ease_forwards]">
            Somewhere in the Mossy Courtyard · before everything
          </div>
          <div className="my-12 text-7xl animate-[colPigeonFly_3s_linear_forwards]">
            🕊️
          </div>
          <p className="text-sm sm:text-base text-neutral-400 max-w-md animate-[colFadeIn_0.9s_ease_0.5s_forwards] opacity-0 italic">
            A pigeon flies overhead. <span className="text-neutral-500">You think nothing of it.</span>
          </p>
        </SceneFrame>
      )}

      {scene === "silver_light" && (
        <SceneFrame testid="scene-silver-light" bg="from-slate-900 via-slate-800 to-black">
          <div className="text-[10px] uppercase tracking-[0.5em] text-slate-300 animate-[colFadeIn_0.6s_ease_forwards]">
            ✨ The Silver Light descends
          </div>
          <div className="my-4 text-8xl animate-[colSilverGlow_2.4s_ease_infinite] drop-shadow-[0_0_40px_rgba(200,220,255,0.9)]">
            ✨
          </div>
          <p className="text-xs sm:text-sm text-slate-200 italic max-w-xl mb-4 animate-[colFadeIn_0.8s_ease_0.4s_forwards] opacity-0">
            &ldquo;Unbound — listen well. The cage of time has set its eyes on you. Before the Chronophage finds you, learn to fight.&rdquo;
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 max-w-5xl w-full animate-[colFadeIn_0.8s_ease_0.9s_forwards] opacity-0">
            <TutorialCard testid="tutorial-generator" icon="⚡" title="GENERATOR" body="Your engine. Basic strikes build mana here. No Generator equipped = no skills. You start gifted with one." />
            <TutorialCard testid="tutorial-passive" icon="🛡️" title="PASSIVE (MIND)" body="Always-on defense. Reacts automatically when you're hurt. You start gifted with one." />
            <TutorialCard testid="tutorial-active" icon="⚔️" title="ACTIVE (BODY)" body="Mana-priced strikes. Spend the mana your Generator built to burn enemies down faster." />
            <TutorialCard testid="tutorial-ultimate" icon="✨" title="ULTIMATE (SOUL)" body="The Trinity finisher — needs your Generator + a Mind passive + a Body active. Save it for the killing blow." />
            <TutorialCard testid="tutorial-combo" icon="🤝" title="COMBO ULTIMATE" body="Bring two heroes into a party and unlock shared finishers. 60s cooldown, no rank scaling." />
          </div>
          <button
            type="button"
            onClick={() => setScene("corridor")}
            data-testid="silver-light-continue-btn"
            className="mt-6 px-6 py-2 rounded-lg border border-slate-400/60 bg-slate-900/40 text-slate-200 hover:bg-slate-800 text-sm font-bold animate-[colFadeIn_0.8s_ease_1.8s_forwards] opacity-0"
          >
            I am ready ›
          </button>
        </SceneFrame>
      )}

      {scene === "corridor" && (
        <SceneFrame testid="scene-corridor" bg="from-black via-stone-950 to-black">
          <div className="text-[10px] uppercase tracking-[0.5em] text-amber-300/60 animate-[colFadeIn_1s_ease_forwards]">
            Kingdom of Dragonious · After The Sundering
          </div>
          <h1 className="text-5xl sm:text-7xl font-black mt-4 animate-[colFadeIn_1.2s_ease_0.5s_forwards] opacity-0 drop-shadow-[0_0_30px_rgba(252,211,77,0.4)]">
            THE CORRIDOR <br/>OF SHADOWS
          </h1>
          <p className="mt-8 text-sm sm:text-base text-neutral-300 max-w-xl animate-[colFadeIn_1.2s_ease_1.6s_forwards] opacity-0 leading-relaxed">
            You are walking down a narrow stone corridor. Torches gutter on either side, but the light never quite catches the walls. The floor behind you is <span className="text-red-300">no longer there</span>. Something is eating it.
          </p>
        </SceneFrame>
      )}

      {scene === "chronophage" && (
        <SceneFrame testid="scene-chronophage" bg="from-stone-950 via-red-950/40 to-black">
          <div className="text-[10px] uppercase tracking-[0.5em] text-red-300/80 animate-[colFadeIn_0.8s_ease_forwards]">
            ↓ Something approaches from behind
          </div>
          <h2 className="text-4xl sm:text-6xl font-black mt-3 text-red-200 animate-[colFadeIn_1s_ease_0.4s_forwards] opacity-0 drop-shadow-[0_0_30px_rgba(248,113,113,0.5)]">
            THE CHRONOPHAGE
          </h2>
          <p className="mt-6 text-sm sm:text-base text-neutral-300 max-w-xl animate-[colFadeIn_1s_ease_1s_forwards] opacity-0 leading-relaxed">
            <span className="text-amber-200">It is not chasing you.</span> It is <em>physics</em>. A cosmic clean-up mechanism. When a timeline becomes too fractured, it manifests to <span className="text-red-300 font-bold">delete the sector</span>.
            <br/><br/>
            The corridor behind you is being deleted. You run.
          </p>
        </SceneFrame>
      )}

      {scene === "mirror" && (
        <SceneFrame testid="scene-mirror" bg="from-stone-950 via-indigo-950/40 to-black">
          <div className="text-[10px] uppercase tracking-[0.5em] text-cyan-300/80 animate-[colFadeIn_0.6s_ease_forwards]">
            Ahead — a shimmer in the air
          </div>
          <div className="my-6 text-7xl animate-[colMirrorPulse_2s_ease_infinite]">
            🪞
          </div>
          <p className="text-sm sm:text-base text-neutral-300 max-w-xl animate-[colFadeIn_1s_ease_0.6s_forwards] opacity-0 leading-relaxed">
            A <span className="text-cyan-200">temporal rift</span> opens at the corridor&rsquo;s end. One of the few breaches still leaking through The Sundering. It is unstable, ancient, and <em>open</em>.
            <br/><br/>
            The Chronophage closes the distance.
          </p>
        </SceneFrame>
      )}

      {scene === "choice" && (
        <SceneFrame testid="scene-choice" bg="from-stone-950 via-stone-900 to-black">
          <div className="text-[10px] uppercase tracking-[0.5em] text-amber-300/70 animate-[colFadeIn_0.6s_ease_forwards]">
            Decide. Now.
          </div>
          <h2 className="text-3xl sm:text-5xl font-black mt-3 text-amber-100 animate-[colFadeIn_0.8s_ease_0.3s_forwards] opacity-0">
            DO YOU GO OR STAY?
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-8 max-w-2xl w-full animate-[colFadeIn_0.8s_ease_0.8s_forwards] opacity-0">
            <ChoiceCard onClick={() => pick("mirror")} disabled={busy} testid="choice-mirror-btn"
              icon="🪞" title="GO" subtitle="Lunge through the Mirror"
              blurb="Trust the rift. Whatever is on the other side, it isn't HERE." tone="cyan" />
            <ChoiceCard onClick={() => pick("void")} disabled={busy} testid="choice-void-btn"
              icon="🌑" title="STAY" subtitle="Face the Chronophage"
              blurb="Plant your feet. Some things deserve to be looked at, even by the dying." tone="red" />
          </div>
        </SceneFrame>
      )}

      {scene === "path" && (
        <SceneFrame testid="scene-path" bg={choice === "mirror" ? "from-cyan-950/40 via-indigo-950/60 to-black" : "from-red-950/50 via-stone-950 to-black"}>
          <div className="text-[10px] uppercase tracking-[0.5em] text-amber-300/60 animate-[colFadeIn_0.5s_ease_forwards]">
            {choice === "mirror" ? "Through the rift…" : "You stand your ground…"}
          </div>
          <div className="text-9xl my-6 animate-[colPathSpin_2s_ease_forwards]">
            {choice === "mirror" ? "🪞" : "🌑"}
          </div>
          <p className="text-sm text-neutral-300 max-w-md animate-[colFadeIn_0.8s_ease_0.4s_forwards] opacity-0">
            {choice === "mirror"
              ? "The mirror shatters into seven directions of reflected light. You are pulled through."
              : "The Chronophage washes over you. Everything becomes nothing. Nothing becomes everything."
            }
          </p>
        </SceneFrame>
      )}

      {scene === "gasp" && (
        <div data-testid="scene-gasp" className="absolute inset-0 bg-black flex items-center justify-center">
          <div className="text-8xl sm:text-9xl font-black text-amber-100 animate-[colGasp_1.6s_ease_forwards] drop-shadow-[0_0_40px_rgba(252,211,77,0.7)]"
               style={{ fontFamily: "Anton, sans-serif" }}>
            *GASP*
          </div>
        </div>
      )}

      {/* DREAM SPAWN — Mossy Courtyard. Still dreaming. */}
      {scene === "spawn" && completeResult && (
        <SceneFrame testid="scene-spawn" bg="from-emerald-950/40 via-stone-950 to-black">
          <div className="text-[10px] uppercase tracking-[0.5em] text-emerald-300/80 animate-[colFadeIn_0.6s_ease_forwards]">
            You wake · or do you?
          </div>
          <div className="text-7xl my-4 animate-[colFadeIn_0.8s_ease_0.3s_forwards] opacity-0">
            {completeResult.spawn_icon}
          </div>
          <h2 className="text-3xl sm:text-5xl font-black text-amber-100 animate-[colFadeIn_0.8s_ease_0.5s_forwards] opacity-0">
            {completeResult.spawn_label?.toUpperCase()}
          </h2>
          <p className="mt-4 text-sm sm:text-base text-neutral-300 max-w-xl animate-[colFadeIn_0.9s_ease_0.9s_forwards] opacity-0 leading-relaxed">
            {completeResult.spawn_blurb}
          </p>

          <div className="mt-6 rounded-xl border border-amber-700/60 bg-black/60 p-4 max-w-md animate-[colFadeIn_0.9s_ease_1.3s_forwards] opacity-0">
            <div className="text-[10px] uppercase tracking-[0.3em] text-amber-300/70 mb-2">Granted</div>
            <div className="space-y-1 text-sm">
              <div className="flex items-center gap-2"><span className="text-2xl">🗡️</span> Unbound Initiate&rsquo;s Blade <span className="text-[10px] text-neutral-500">(Lv 1)</span></div>
              <div className="flex items-center gap-2"><span className="text-2xl">🧥</span> Unbound Initiate&rsquo;s Cloak <span className="text-[10px] text-neutral-500">(Lv 1)</span></div>
            </div>
          </div>

          <div className="mt-5 text-xs sm:text-sm italic text-amber-200/70 max-w-md animate-[colFadeIn_0.9s_ease_1.7s_forwards] opacity-0">
            …but the moss feels wrong. The pond&rsquo;s ripples carry no sound. <span className="text-amber-300">You are still dreaming.</span>
          </div>

          <button
            onClick={goToTomb}
            disabled={busy}
            data-testid="continue-to-tomb-btn"
            className="mt-6 px-6 py-2.5 rounded-lg bg-stone-800 hover:bg-stone-700 border border-amber-700/60 text-amber-100 text-sm font-black tracking-wide animate-[colFadeIn_0.9s_ease_2.1s_forwards] opacity-0 disabled:opacity-50"
          >
            … wake up ›
          </button>
        </SceneFrame>
      )}

      {/* ════ ACT 2 — REAL AWAKENING AT THE TOMB ════ */}

      {scene === "tomb" && tombResult && (
        <SceneFrame testid="scene-tomb" bg="from-amber-950/40 via-stone-900 to-black">
          <div className="text-[10px] uppercase tracking-[0.5em] text-amber-300/70 animate-[colFadeIn_0.5s_ease_forwards]">
            ⚰️ The veil is gone — this is real
          </div>
          <div className="text-7xl my-3 animate-[colFadeIn_0.7s_ease_0.2s_forwards] opacity-0">
            {tombResult.tomb?.spawn_icon}
          </div>
          <h2 className="text-3xl sm:text-5xl font-black text-amber-100 animate-[colFadeIn_0.7s_ease_0.4s_forwards] opacity-0">
            {tombResult.tomb?.spawn_label?.toUpperCase()}
          </h2>
          <p className="mt-3 text-sm sm:text-base text-neutral-300 max-w-xl animate-[colFadeIn_0.9s_ease_0.7s_forwards] opacity-0 leading-relaxed">
            {tombResult.tomb?.spawn_blurb}
          </p>

          <div className="my-4 text-5xl animate-[colSilverGlow_2.4s_ease_infinite] drop-shadow-[0_0_40px_rgba(200,220,255,0.9)]">
            ✨
          </div>
          <p className="text-xs sm:text-sm text-slate-200 italic max-w-lg animate-[colFadeIn_0.8s_ease_1.1s_forwards] opacity-0">
            &ldquo;What you saw in the dream — that is the cage. This place is your bedrock. The Tomb of Awakening remembers every Unbound it has ever woken. It will remember you.&rdquo;
          </p>

          {/* Journals written */}
          <div className="mt-6 rounded-xl border border-amber-700/40 bg-black/50 p-4 max-w-2xl w-full animate-[colFadeIn_0.9s_ease_1.5s_forwards] opacity-0">
            <div className="text-[10px] uppercase tracking-[0.3em] text-amber-300/80 mb-2">
              📜 Journal entries added
            </div>
            <div className="space-y-3 text-left">
              {Object.entries(tombResult.journals || {}).map(([npc, j]) => (
                <div key={npc} data-testid={`journal-${npc}`} className="border-l-2 border-amber-600/40 pl-3">
                  <div className="text-xs text-amber-200 font-bold">{j.author}</div>
                  <div className="text-[11px] text-amber-100/80 italic mb-1">{j.title}</div>
                  <div className="text-[11px] text-neutral-300 leading-snug">{j.body}</div>
                </div>
              ))}
            </div>
          </div>

          <button
            onClick={goToClassPick}
            disabled={busy}
            data-testid="continue-to-class-pick-btn"
            className="mt-6 px-6 py-2.5 rounded-lg bg-amber-800/60 hover:bg-amber-700 border border-amber-500/60 text-amber-100 text-sm font-black tracking-wide animate-[colFadeIn_0.9s_ease_1.9s_forwards] opacity-0 disabled:opacity-50"
          >
            ✨ the light speaks again ›
          </button>
        </SceneFrame>
      )}

      {scene === "class_pick" && (
        <SceneFrame testid="scene-class-pick" bg="from-slate-900 via-stone-950 to-black">
          <div className="text-[10px] uppercase tracking-[0.5em] text-slate-300 animate-[colFadeIn_0.5s_ease_forwards]">
            ✨ Choose your path
          </div>
          <div className="my-3 text-5xl animate-[colSilverGlow_2.4s_ease_infinite] drop-shadow-[0_0_40px_rgba(200,220,255,0.9)]">
            ✨
          </div>
          <h2 className="text-2xl sm:text-4xl font-black text-amber-100 animate-[colFadeIn_0.7s_ease_0.3s_forwards] opacity-0">
            THE PHOENIX TRANSFORMATION
          </h2>
          <p className="text-xs sm:text-sm text-slate-200 italic max-w-xl mt-3 animate-[colFadeIn_0.8s_ease_0.6s_forwards] opacity-0">
            &ldquo;To unlock and unleash your full potential in this new world, you must be reborn as a Phoenix. There is no other way. Pick the form your fire takes.&rdquo;
          </p>
          <div className="mt-3 text-[10px] uppercase tracking-[0.3em] text-red-300/70 animate-[colFadeIn_0.8s_ease_0.9s_forwards] opacity-0">
            ⚠ This choice is permanent — choose with intent
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mt-6 max-w-5xl w-full animate-[colFadeIn_0.9s_ease_1.2s_forwards] opacity-0">
            {CLASS_CARDS.map((c) => (
              <ClassCard
                key={c.id}
                onClick={() => pickClass(c.id)}
                disabled={busy}
                testid={`class-pick-${c.id}-btn`}
                {...c}
              />
            ))}
          </div>

          <div className="mt-5 text-[10px] text-neutral-500 italic animate-[colFadeIn_0.9s_ease_1.6s_forwards] opacity-0">
            🔒 Blue Mage and Cassandra are sealed for now — their stories will find you later.
          </div>
        </SceneFrame>
      )}

      {scene === "phoenix_transform" && classPickResult && (
        <div data-testid="scene-phoenix-transform" className="absolute inset-0 bg-black flex items-center justify-center overflow-hidden">
          <div className="absolute inset-0 bg-gradient-radial from-amber-500/30 via-red-900/20 to-black animate-[colFadeIn_0.5s_ease_forwards]" />
          <div className="relative text-center">
            <div className="text-9xl animate-[colPhoenixBurst_2.4s_ease_forwards] drop-shadow-[0_0_60px_rgba(252,165,77,0.9)]">
              🔥🐦‍🔥🔥
            </div>
            <div className="mt-4 text-4xl sm:text-6xl font-black text-amber-100 animate-[colFadeIn_0.7s_ease_0.6s_forwards] opacity-0"
                 style={{ fontFamily: "Anton, sans-serif" }}>
              YOU ARE PHOENIX-BLOODED
            </div>
            <div className="mt-2 text-sm text-amber-300/90 animate-[colFadeIn_0.7s_ease_1.1s_forwards] opacity-0">
              {classPickResult.picked ? `Class locked · ${classPickResult.picked.replace("_", " ").toUpperCase()}` : ""}
            </div>
          </div>
        </div>
      )}

      {/* ════ ACT 3 — CASTLE & THE FARM ════ */}

      {scene === "castle" && (
        <SceneFrame testid="scene-castle" bg="from-amber-900/40 via-stone-900 to-black">
          <div className="text-[10px] uppercase tracking-[0.5em] text-amber-300/70 animate-[colFadeIn_0.5s_ease_forwards]">
            🏰 You wake — warm stone, sunlight
          </div>
          <div className="text-7xl my-3 animate-[colFadeIn_0.7s_ease_0.2s_forwards] opacity-0">
            🏰
          </div>
          <h2 className="text-3xl sm:text-5xl font-black text-amber-100 animate-[colFadeIn_0.7s_ease_0.4s_forwards] opacity-0">
            KINGDOM OF DRAGONIOUS
          </h2>
          <div className="text-sm text-amber-200/80 animate-[colFadeIn_0.7s_ease_0.7s_forwards] opacity-0">
            Castle Grounds
          </div>
          <p className="mt-4 text-sm sm:text-base text-neutral-300 max-w-xl animate-[colFadeIn_0.9s_ease_1s_forwards] opacity-0 leading-relaxed">
            Phoenix-fire fades from your hands. You stand on warm flagstones in the shadow of the great castle. The Tomb is far behind you now.
            <br/><br/>
            A small leather-bound book lies open at your feet, pages fluttering in the breeze.
          </p>
          <button
            onClick={goToGreenThumb}
            disabled={busy}
            data-testid="pickup-green-thumb-btn"
            className="mt-6 px-6 py-2.5 rounded-lg bg-emerald-900/60 hover:bg-emerald-800 border border-emerald-500/60 text-emerald-100 text-sm font-black tracking-wide animate-[colFadeIn_0.9s_ease_1.4s_forwards] opacity-0 disabled:opacity-50"
          >
            📖 pick up the book ›
          </button>
        </SceneFrame>
      )}

      {scene === "green_thumb" && (
        <SceneFrame testid="scene-green-thumb" bg="from-emerald-950/50 via-stone-900 to-black">
          <div className="text-[10px] uppercase tracking-[0.5em] text-emerald-300/80 animate-[colFadeIn_0.5s_ease_forwards]">
            📖 The book reads itself open
          </div>
          <div className="text-7xl my-3 animate-[colFadeIn_0.7s_ease_0.2s_forwards] opacity-0">
            📖
          </div>
          <h2 className="text-3xl sm:text-5xl font-black text-emerald-100 animate-[colFadeIn_0.7s_ease_0.4s_forwards] opacity-0">
            THE GREEN THUMB
          </h2>
          <div className="my-4 text-5xl animate-[colSilverGlow_2.4s_ease_infinite] drop-shadow-[0_0_40px_rgba(200,220,255,0.9)] opacity-0 animate-[colFadeIn_0.9s_ease_0.8s_forwards]">
            ✨
          </div>
          <p className="text-xs sm:text-sm text-slate-200 italic max-w-xl mt-2 animate-[colFadeIn_0.9s_ease_1.1s_forwards] opacity-0">
            &ldquo;Every Phoenix needs roots. The <span className="text-emerald-300 font-bold">Farm</span> is yours. Till it. Plant. Harvest. The Kingdom runs on Chits, and Chits grow from soil.&rdquo;
          </p>
          <p className="text-[11px] text-neutral-400 max-w-md mt-3 animate-[colFadeIn_0.9s_ease_1.6s_forwards] opacity-0">
            🌾 Crops you grow can be sold, cooked into Diner meals, or burned as offerings at the Deity Altar.
          </p>
          <button
            onClick={goToCari}
            disabled={busy}
            data-testid="continue-to-cari-btn"
            className="mt-6 px-6 py-2.5 rounded-lg bg-emerald-800/60 hover:bg-emerald-700 border border-emerald-400/60 text-emerald-50 text-sm font-black tracking-wide animate-[colFadeIn_0.9s_ease_2.1s_forwards] opacity-0 disabled:opacity-50"
          >
            close the book ›
          </button>
        </SceneFrame>
      )}

      {scene === "cari" && (
        <SceneFrame testid="scene-cari" bg="from-amber-900/30 via-emerald-950/30 to-black">
          <div className="text-[10px] uppercase tracking-[0.5em] text-amber-300/70 animate-[colFadeIn_0.5s_ease_forwards]">
            … you turn and bump into someone
          </div>
          <div className="text-7xl my-3 animate-[colFadeIn_0.7s_ease_0.2s_forwards] opacity-0">
            👵
          </div>
          <h2 className="text-2xl sm:text-4xl font-black text-amber-100 animate-[colFadeIn_0.7s_ease_0.4s_forwards] opacity-0">
            GENERAL CARI
          </h2>
          <p className="mt-4 text-sm sm:text-base text-amber-100/90 italic max-w-xl animate-[colFadeIn_0.9s_ease_0.8s_forwards] opacity-0 leading-relaxed">
            &ldquo;Oh thank the Phoenix — someone who hasn&rsquo;t forgotten how to garden AND keep their Chits! Come, come, stranger. The Farm&rsquo;s been waiting for a hand it can trust.&rdquo;
          </p>
          <p className="mt-3 text-xs sm:text-sm text-amber-200/80 italic max-w-xl animate-[colFadeIn_0.9s_ease_1.1s_forwards] opacity-0 leading-relaxed">
            &ldquo;When you&rsquo;re settled — go find <span className="text-amber-300 font-bold">Roc</span> at the Gym. He likes to make a proper ceremony out of your class. The Silver Light rushed you. Roc never rushes.&rdquo;
          </p>
          <p className="mt-3 text-[11px] text-neutral-400 max-w-md animate-[colFadeIn_0.9s_ease_1.5s_forwards] opacity-0">
            She pats your shoulder. You can feel the dirt under her fingernails. Real dirt. Not dream-dirt.
          </p>
          <button
            onClick={onEnter}
            disabled={busy}
            data-testid="enter-dragonious-btn"
            className="mt-8 px-8 py-3 rounded-lg bg-amber-700 hover:bg-amber-600 text-white text-base font-black tracking-wide animate-[colFadeIn_0.9s_ease_2.0s_forwards] opacity-0 disabled:opacity-50"
          >
            ⚔️ ENTER DRAGONIOUS
          </button>
        </SceneFrame>
      )}
    </div>
  );
}

function TutorialCard({ icon, title, body, testid }) {
  return (
    <div data-testid={testid} className="rounded-xl border-2 border-slate-500/40 bg-slate-950/40 p-3 backdrop-blur-sm">
      <div className="text-3xl mb-1">{icon}</div>
      <div className="text-sm font-black tracking-wider text-amber-100" style={{ fontFamily: "Anton, sans-serif" }}>{title}</div>
      <div className="text-[11px] text-slate-300 mt-1 leading-snug">{body}</div>
    </div>
  );
}

function SceneFrame({ children, bg, testid }) {
  return (
    <div data-testid={testid} className={`absolute inset-0 bg-gradient-to-b ${bg} flex flex-col items-center justify-center px-6 text-center overflow-y-auto py-12`}>
      <div className="max-w-3xl w-full flex flex-col items-center">{children}</div>
    </div>
  );
}

function ChoiceCard({ onClick, disabled, testid, icon, title, subtitle, blurb, tone }) {
  const toneClass = tone === "cyan"
    ? "border-cyan-700/60 hover:border-cyan-400 from-cyan-950/40 to-black hover:shadow-[0_0_30px_-8px_rgba(34,211,238,0.6)]"
    : "border-red-700/60 hover:border-red-400 from-red-950/40 to-black hover:shadow-[0_0_30px_-8px_rgba(248,113,113,0.6)]";
  return (
    <button type="button" onClick={onClick} disabled={disabled} data-testid={testid}
      className={`text-left rounded-2xl border-2 ${toneClass} bg-gradient-to-br p-5 transition-all hover:scale-[1.02] disabled:opacity-40 disabled:cursor-not-allowed`}>
      <div className="flex items-center gap-3 mb-2">
        <div className="text-5xl">{icon}</div>
        <div>
          <div className="text-3xl font-black text-amber-100" style={{ fontFamily: "Anton, sans-serif" }}>{title}</div>
          <div className="text-[10px] uppercase tracking-widest text-neutral-400">{subtitle}</div>
        </div>
      </div>
      <p className="text-xs text-neutral-300 leading-relaxed mt-3">{blurb}</p>
    </button>
  );
}

function ClassCard({ onClick, disabled, testid, icon, name, tagline, blurb, tone }) {
  return (
    <button type="button" onClick={onClick} disabled={disabled} data-testid={testid}
      className={`text-left rounded-2xl border-2 ${tone} bg-gradient-to-br p-4 transition-all hover:scale-[1.03] disabled:opacity-40 disabled:cursor-not-allowed`}>
      <div className="text-5xl mb-2">{icon}</div>
      <div className="text-2xl font-black text-amber-100" style={{ fontFamily: "Anton, sans-serif" }}>{name}</div>
      <div className="text-[10px] uppercase tracking-widest text-neutral-400 mt-0.5 mb-2">{tagline}</div>
      <p className="text-[11px] text-neutral-300 leading-snug">{blurb}</p>
    </button>
  );
}
